import { useState } from 'react'

import { Box, Button, Divider, Grid, Section, Text } from '@factorialco/deprecated-design-system'
import {
  ApprovalsDomainConfigurationOperationsEnum,
  ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum,
  DomainConfigurationRulesDetailsFragment,
  RuleconfigurationDetailsRuleFragment,
} from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { ApprovalFlowArgs } from 'modules/approvals/lib/approvalFlows'

import { OnRulesMet } from './OnRulesMet'

type Action = 'create' | 'update'

export interface rulesConfigurationsFormValues {
  name: string
  isTemplate: boolean
  rules?: Omit<RuleconfigurationDetailsRuleFragment, 'id'>[]
  onRulesMet: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum
  flow: ApprovalFlowArgs | null
}

interface Props {
  action: Action
  variables: rulesConfigurationsFormValues
  domainConfigurationRules: DomainConfigurationRulesDetailsFragment[]
  onSubmit: (data: rulesConfigurationsFormValues) => Promise<UnhandledResult<Result<unknown>>>
  onSuccess: (data: rulesConfigurationsFormValues) => void
}

export const Form = ({
  action,
  variables,
  domainConfigurationRules,
  onSubmit,
  onSuccess,
}: Props) => {
  const form = useForm<rulesConfigurationsFormValues>({
    defaultValues: {
      name: variables.name,
      isTemplate: variables.isTemplate,
      rules: variables.rules,
      onRulesMet: variables.onRulesMet,
      flow: variables.flow,
    },
    onSubmit: async (data) => {
      const result = await onSubmit({
        ...data,
        flow: rulesMetData.flow,
        onRulesMet: rulesMetData.option,
      })
      const handled = handleResult(result, (formErrors) => {
        form.setErrors({ name: formErrors.name })
      })

      if (handled.success) {
        setSuccessMessage()
        onSuccess(data)
      }

      return handled
    },
  })

  const [rulesMetData, setRulesMetData] = useState<{
    flow: ApprovalFlowArgs | null
    option: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum
  }>({
    flow: variables.flow ?? null,
    option: 'auto_approve',
  })

  const onRulesMetData = (
    flow: ApprovalFlowArgs | null,
    option: ApprovalsValueObjectsRulesConfigurationRuleDecisionEnum
  ) => {
    setRulesMetData({ flow, option })
  }

  const invalidValues = (values: string[]) => {
    if (values.length === 0) {
      return true
    }

    const excludedValues = ['0', '']
    return values.some((value) => excludedValues.includes(value))
  }

  const missingMandatoryRules = () => {
    const mandatoryRules = domainConfigurationRules.filter((rule) => rule.mandatory)
    const invalidRules = mandatoryRules.filter((rule) => {
      const ruleValue =
        form.values.rules?.find((formRule) => formRule.identifier == rule.identifier)?.value || []
      return invalidValues(ruleValue)
    })
    return invalidRules.length > 0
  }

  const allRulesEmpty = () => {
    return form.values.rules?.every((rule) => {
      return invalidValues(rule.value)
    })
  }

  const submitDisabled = () => {
    return missingMandatoryRules() || allRulesEmpty() || !form.values.name || form.isSubmitting
  }

  const setSuccessMessage = () => {
    const message =
      action == 'create'
        ? i18n.t('settings.approvals.approval_rule.add.success')
        : i18n.t('settings.approvals.approval_rule.edit.success')

    app.setFlash({
      type: 'correct',
      message: message,
      time: 2000,
    })
  }

  const addRule = (
    value: string[],
    operation: ApprovalsDomainConfigurationOperationsEnum,
    domainRule: DomainConfigurationRulesDetailsFragment
  ) => {
    const newRule = {
      identifier: domainRule.identifier,
      label: domainRule.label,
      inputType: domainRule.inputType,
      value: value,
      operation,
    }
    const rules = form.values.rules?.filter((rule) => rule.identifier !== newRule.identifier) || []
    rules.push(newRule)
    form.setValues({ ...form.values, rules: rules })
  }

  const convertCentsValue = (value: string) => {
    if (value === '') return '0'
    return String(parseInt(value) * 100)
  }

  const convertCentsToUI = (value: string) => {
    return String(parseInt(value) / 100)
  }

  const printBoxInput = (rule: DomainConfigurationRulesDetailsFragment) => {
    const col = rule.inputWidth === 'half' ? 6 : 12
    const options = availableOptions(rule)

    if (options.length == 0 && rule.inputType == 'selector') {
      return
    }
    return (
      <Box key={rule.identifier} gridColSpan={{ xs: col }}>
        {printInput(rule, options)}
      </Box>
    )
  }

  const availableOptions = (rule: DomainConfigurationRulesDetailsFragment) => {
    if (!rule.parentRuleIdentifier) {
      return rule.selectorOptions
    }

    const selected_values =
      form.values.rules
        ?.filter((form_rule) => {
          return form_rule.identifier == rule.parentRuleIdentifier
        })
        .flatMap((form_rule) => form_rule.value) || []

    return rule.selectorOptions.filter((option) => {
      return selected_values.includes(option.parentId || '')
    })
  }

  const printInput = (
    rule: DomainConfigurationRulesDetailsFragment,
    options: DomainConfigurationRulesDetailsFragment['selectorOptions']
  ) => {
    let value = null
    switch (rule.inputType) {
      case 'single_choice':
        value = form.values.rules?.find((r) => r.identifier == rule.identifier)?.value || []

        return (
          <Selector.Popover
            headerLabel={i18n.t('common.all')}
            allSelectedLabel={i18n.t('common.all')}
            key={rule.identifier}
            placeholder={''}
            label={rule.label}
            searchPlaceholder={rule.label}
            blankLabel={rule.label}
            value={value}
            mandatory={rule.mandatory}
            loading={form.isSubmitting}
            onChange={(value) => addRule([value] as string[], rule.operation, rule)}
          >
            {rule.selectorOptions.map((option) => (
              <Selector.Option
                key={option.value}
                value={option.value}
                label={option.label}
                searchValue={option.label}
              >
                {option.label}
              </Selector.Option>
            ))}
          </Selector.Popover>
        )
      case 'selector':
        value = form.values.rules?.find((r) => r.identifier == rule.identifier)?.value || []
        if (options.length === 0) {
          return <></>
        }
        return (
          <Selector.Popover
            headerLabel={i18n.t('common.all')}
            allSelectedLabel={i18n.t('common.all')}
            key={rule.identifier}
            placeholder={''}
            label={rule.label}
            searchPlaceholder={rule.label}
            blankLabel={rule.label}
            value={value}
            mandatory={rule.mandatory}
            loading={form.isSubmitting}
            onChange={(value) => addRule(value as string[], rule.operation, rule)}
            isMultiSelect
          >
            {options.map((option) => (
              <Selector.Option
                key={option.value}
                value={option.value}
                label={option.label}
                searchValue={option.label}
              >
                {option.label}
              </Selector.Option>
            ))}
          </Selector.Popover>
        )
      case 'cents':
        value = form.values.rules?.find((r) => r.identifier == rule.identifier)?.value || ['0']
        return (
          <TextField
            key={rule.identifier}
            value={convertCentsToUI(value[0] || '0')}
            mandatory={rule.mandatory}
            loading={form.isSubmitting}
            label={rule.label}
            type="number"
            max={999999999}
            onChange={(value) => addRule([convertCentsValue(value)], rule.operation, rule)}
          />
        )
    }
  }

  const formTitle = () => {
    switch (action) {
      case 'create':
        return i18n.t('settings.approvals.approval_rule.add.title')
      case 'update':
        return i18n.t('settings.approvals.approval_rule.edit.title')
    }
  }

  return (
    <form onSubmit={form.handleSubmit}>
      <Box>
        <Section
          illustration="monitor-flash"
          title={formTitle()}
          description={i18n.t('settings.approvals.approval_rule.add.description')}
          paddingX="s0"
          paddingY="s0"
        >
          <Box paddingTop="s40" gap="s16" paddingBottom="s32">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('settings.approvals.approval_rule.add.name')}
            </Text>
            <TextField
              mandatory
              loading={form.isSubmitting}
              value={form.values.name}
              onChange={(name) => form.setValues({ ...form.values, name })}
              error={form.errors.name}
            />
          </Box>
          <Divider />
          <Box paddingTop="s32" gap="s16">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('settings.approvals.approval_rule.conditions.title')}
            </Text>
            <Grid variant="fixed">
              {domainConfigurationRules.map((rule) => printBoxInput(rule))}
            </Grid>
          </Box>
        </Section>

        <Section
          illustration="network"
          title={i18n.t('settings.approvals.approval_rule.configuration.title')}
          description={i18n.t('settings.approvals.approval_rule.configuration.description')}
          paddingX="s0"
          paddingY="s32"
        >
          <Divider />
          <Box paddingTop="s32">
            <Text size="300" weight="semibold" color="grey1000">
              {i18n.t('settings.approvals.approval_rule.configuration.title')}
            </Text>
            <OnRulesMet
              ruleName={form.values.name}
              onRulesMetValue={variables.onRulesMet}
              flow={variables.flow}
              onChange={onRulesMetData}
            />
          </Box>
          <Box flexDirection="row" paddingY="s12" justifyContent="flexEnd">
            <Button disabled={submitDisabled()} submit>
              {i18n.t('common.save')}
            </Button>
          </Box>
        </Section>
      </Box>
    </form>
  )
}
